<template>
  <StoryblokBackgroundImageCard
    v-if="useBackgroundCard"
    :blok="blok"
    :variant="backgroundCardVariant"
  />
  <div
    v-else-if="blok.text || blok.image || blok.title"
    v-editable="blok"
    class="card"
    :class="{ 'card--clickable': isCardClickable }"
    :role="isCardClickable ? 'link' : undefined"
    :tabindex="isCardClickable ? 0 : undefined"
    @click="openCardLink"
    @keydown.enter.prevent="openCardLink"
    @keydown.space.prevent="openCardLink"
  >
    <EMCard :border="true" :no-shadow="false" :no-padding="true">
      <template #default>
        <article class="card-content" :class="cardContentClass">
          <img
            v-if="blok.image && blok.image?.filename.length > 0"
            :src="blok.image.filename"
            :alt="blok.imageAlt || blok.image.alt || ''"
            class="card-image"
          />
          <div class="card-body">
            <StoryblokRichText v-if="richTextBlok" :blok="richTextBlok" />
            <p v-else-if="plainText">{{ plainText }}</p>
          </div>
        </article>
      </template>
      <template #footer>
        <div v-if="buttonBlok" class="card-footer">
          <StoryblokButton :blok="buttonBlok" />
        </div>
      </template>
    </EMCard>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import StoryblokBackgroundImageCard from '@/features/storyblok/components/StoryblokBackgroundImageCard.vue';
import StoryblokButton from '@/features/storyblok/components/StoryblokButton.vue';
import StoryblokRichText from '@/features/storyblok/components/StoryblokRichText.vue';
import EMCard from '@/common/components/ui/card/EMCard.vue';
import { computed } from 'vue';
import { CommonRoutesName } from '@/router/constants/routesName';
import {
  LinkType,
  type StoryblokButtonBlok,
  type StoryblokCardBlok,
  type StoryblokRichTextBlok,
} from '@/features/storyblok/types';
import {
  extractStorySlugFromWardPath,
  resolveStoryblokLink,
} from '@/features/storyblok/utils/link';

const props = defineProps<{
  blok: StoryblokCardBlok;
  displayMode?: 'card' | 'slider';
}>();

const router = useRouter();

const cardContentClass = computed(() => `card-content--${props.blok.imagePosition ?? 'top'}`);
const useBackgroundCard = computed(
  () => props.displayMode === 'slider' || props.blok.imageBackground === true
);
const backgroundCardVariant = computed<'hero' | 'compact'>(() =>
  props.displayMode === 'slider' ? 'hero' : 'compact'
);

const richTextBlok = computed<StoryblokRichTextBlok | null>(() =>
  typeof props.blok.text === 'string' || !props.blok.text
    ? null
    : {
        content: props.blok.text,
      }
);

const plainText = computed(() =>
  typeof props.blok.text === 'string' ? props.blok.text.trim() : undefined
);

const buttonBlok = computed<StoryblokButtonBlok | null>(() => {
  if (!props.blok.link || !props.blok.buttonTitle) return null;

  return {
    title: props.blok.buttonTitle,
    linkType: props.blok.linkType ?? LinkType.WARD,
    buttonLink: props.blok.link,
    position: props.blok.buttonPosition ?? 'left',
    style: props.blok.buttonStyle ?? 'primary',
  };
});

const cardTarget = computed(() => {
  if (buttonBlok.value || !props.blok.link) {
    return null;
  }

  return resolveStoryblokLink(props.blok.link, props.blok.linkType ?? LinkType.WARD);
});

const isCardClickable = computed(() => !!cardTarget.value);

const openCardLink = (event: MouseEvent | KeyboardEvent): void => {
  if (!cardTarget.value) {
    return;
  }

  const eventTarget = event.target;
  if (
    eventTarget instanceof HTMLElement &&
    eventTarget.closest('a, button, [role="button"], [role="link"]')
  ) {
    return;
  }

  if (cardTarget.value.type === 'external') {
    window.open(cardTarget.value.href, '_blank', 'noopener,noreferrer');
    return;
  }

  if (cardTarget.value.type === 'storyblok') {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: cardTarget.value.slug },
    });
    return;
  }

  const storySlugFromInternalPath = extractStorySlugFromWardPath(cardTarget.value.path);
  if (storySlugFromInternalPath) {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: storySlugFromInternalPath },
    });
    return;
  }

  void router.push(cardTarget.value.path);
};
</script>

<style scoped>
.card {
  /* margin: var(--ion-space-2) 0; */
  height: 100%;
  padding-bottom: var(--ion-space-6);
}

.card--clickable {
  cursor: pointer;
}

.card--clickable:focus-visible {
  outline: 2px solid var(--ion-color-primary);
  outline-offset: 4px;
  border-radius: var(--ion-radius-xl);
}
.card :deep(.em-card) {
  padding: var(--ion-space-2);
}

.card-content {
  display: grid;
  align-items: start;
  height: 100%;
}

.card-content--top,
.card-content--bottom {
  grid-template-columns: minmax(0, 1fr);
}

.card-content--left {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
}

.card-content--right {
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
}

.card-content--top .card-image {
  order: 1;
}

.card-content--top .card-body {
  order: 2;
}

.card-content--bottom .card-body {
  order: 1;
}

.card-content--bottom .card-image {
  order: 2;
}

.card-content--left .card-image {
  order: 1;
}

.card-content--left .card-body {
  order: 2;
}

.card-content--right .card-body {
  order: 1;
}

.card-content--right .card-image {
  order: 2;
}

.card-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: var(--ion-radius-xl);
}

.card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.card-body p {
  margin: 0;
}

.card-body :deep(.card-content) {
  padding-top: 0;
  padding-bottom: 0;
}

.card-body :deep(.rich-text) {
  width: 100%;
  color: var(--ion-text-color);
}

.card-footer {
  padding: 0;
}
.card-footer :deep(.button) {
  padding: 0;
}

.card :deep(.em-card-footer) {
  padding: 0 var(--ion-space-4) var(--ion-space-2) var(--ion-space-4);
}

.card :deep(.em-card) {
  height: 100%;
  min-height: 250px;
}

.card :deep(.em-card-content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

@media (max-width: 768px) {
  .card :deep(.em-card) {
    min-height: 360px;
  }

  .card-content--left,
  .card-content--right {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
